import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FaLightbulb } from 'react-icons/fa';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import Select, { SelectChangeEvent } from '@mui/material/Select';

import { Button as EdsButton } from '@getethos/ethos-design-system-v2';
import { useQuery } from '@tanstack/react-query';
import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { getRuleResultsEndPoint } from '../../../common/api.endPoints';
import { NoraButton } from '../../../common/components/v2';
import {
  DecisionType,
  UpdatedRateClass,
} from '../../../common/constants/decision';
import { ApplicationLifecycle } from '../../../common/constants/lifecycle';
import { UserOrganization } from '../../../common/constants/userOrganization';
import { NoraPermissionV2 } from '../../../common/constants/roles';
import { FetchService } from '../../../common/fetch.service';
import { filterToGuidelineRuleSets } from '../../../common/helpers/rule-results/filter';
import { sortRuleSets } from '../../../common/helpers/rule-results/sort';
import { HttpService } from '../../../common/http.service';
import { useAccess } from '../../../common/hooks/useAccess';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { AuthContext } from '../../login/AuthWrapper';
import { getAllTasks } from '../../tasks/slice/taskListSlice';
import { requestRuleReview } from '../../tasks/slice/taskThunks';
import { TaskTypes } from '../../tasks/types/taskTypes';
import {
  getMessageFromDecision,
  isSaharaDeclineToRuw,
} from '../decision/decisionMessages';
import { IDecision } from '../interfaces/IDecision';
import { IRuleResults, IRuleSet } from '../interfaces/IRuleResults';
import styles from '../styles.module.scss';
import { RuleResultTab, RuleResultTabUnion, RuleResultType } from './constants';
import { RuleResultsView } from './RuleResultsView';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const RuleResults = ({
  applicationId,
  decisions,
  termPolicyId,
}: {
  applicationId: string;
  decisions: IDecision[];
  termPolicyId: number;
}) => {
  const auth = useContext(AuthContext);
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const dispatch = useDispatch<AppDispatch>();

  const [isExpanded, setIsExpanded] = useState(false);

  const [availableRuleResultTypes, setAvailableRuleResultTypes] = useState<
    RuleResultType[]
  >(['Engine']);
  const [selectedRuleResultType, setSelectedRuleResultType] =
    useState<RuleResultType>('Engine');
  const [selectedTab, setSelectedTab] =
    useState<RuleResultTabUnion>('HIGHLIGHT');

  const [isRequestingReview, setIsRequestingReview] = useState(false);
  const [requestDetails, setRequestDetails] = useState('');

  const { data: isGen2 } = useQuery({
    queryKey: ['isGen2', applicationId],
    enabled: isExpanded,
    queryFn: async () => {
      const response = await FetchService.getInstance().get<any>(
        `applications/${applicationId}/policydata`,
      );

      if (response.isGen2 === undefined || response.isGen2 === false) {
        return false;
      }

      return true;
    },
  });

  const {
    data: smartRetryData,
    isLoading: isSmartRetryLoading,
    isError: isSmartRetryError,
  } = useQuery({
    queryFn: async () => {
      const response = await HttpService.getInstance().get(
        getRuleResultsEndPoint(applicationId, 'SmartRetry'),
      );

      if (response && !response.error) {
        const ruleResults: IRuleResults = response.parsedBody;
        return ruleResults;
      } else if (response.error) {
        throw new Error('Failed getting rule results');
      }
    },
    enabled: availableRuleResultTypes.includes('Smart Retry'),
    queryKey: ['ruleResults', applicationId, 'SmartRetry'],
  });

  // Final data is engine data
  const {
    data: finalData,
    isLoading: isFinalLoading,
    isError: isFinalError,
  } = useQuery({
    queryFn: async () => {
      const response = await HttpService.getInstance().get(
        getRuleResultsEndPoint(applicationId, 'Final'),
      );

      if (response && !response.error) {
        const ruleResults: IRuleResults = response.parsedBody;
        return ruleResults;
      }
    },
    queryKey: ['ruleResults', applicationId, 'Final'],
  });

  const saharaDeclineToRuw = useMemo(() => {
    const ruleResults =
      selectedRuleResultType === 'Smart Retry' ? smartRetryData : finalData;
    return isSaharaDeclineToRuw(ruleResults);
  }, [smartRetryData, finalData, selectedRuleResultType]);

  const getMessages = useCallback(() => {
    const engineDecision = decisions.find(
      decision => decision.decisionType === DecisionType.ENGINE,
    );

    const smartRetryDecision = decisions.find(
      decision =>
        decision.decisionType === DecisionType.FINAL && decision.isAutoDecision,
    );

    const newTabMessages = getMessageFromDecision(
      selectedTab === 'SMART_RETRY' ? smartRetryDecision : engineDecision,
      saharaDeclineToRuw,
    );

    return newTabMessages;
  }, [selectedTab, saharaDeclineToRuw]);

  const tabMessages = getMessages();
  const handleRequestReview = () => setIsRequestingReview(true);
  const handleClose = () => setIsRequestingReview(false);

  useEffect(() => {
    decisions.forEach(decision => {
      if (
        decision.decisionType === DecisionType.FINAL &&
        decision.isAutoDecision
      ) {
        const newTypes = ['Engine', 'Smart Retry'];
        setAvailableRuleResultTypes(newTypes as RuleResultType[]);
        if (!availableRuleResultTypes.includes('Smart Retry')) {
          setSelectedRuleResultType('Smart Retry');
        }
      }
    });
  }, [decisions]);

  const hasRidersData = useMemo(() => {
    const ruleResults =
      selectedRuleResultType === 'Smart Retry' ? smartRetryData : finalData;
    if (!ruleResults?.ruleSets) return false;

    return ruleResults.ruleSets.some(ruleSet =>
      ruleSet.matchedRules.some(
        rule => (rule.ineligibleRiders ?? []).length > 0,
      ),
    );
  }, [smartRetryData, finalData, selectedRuleResultType]);

  const tabs: RuleResultTabUnion[] = useMemo(() => {
    const baseTabs: RuleResultTabUnion[] = ['ALL_CONDITIONS', 'HIGHLIGHT'];
    return hasRidersData ? [...baseTabs, 'RIDERS'] : baseTabs;
  }, [hasRidersData]);

  // Get the decision ID for the Decision Graph button based on selected rule result type
  const selectedDecisionId = useMemo(() => {
    if (selectedRuleResultType === 'Smart Retry') {
      const smartRetryDecision = decisions.find(
        decision =>
          decision.decisionType === DecisionType.FINAL &&
          decision.isAutoDecision,
      );
      return smartRetryDecision?.id;
    }
    // Engine
    const engineDecision = decisions.find(
      decision => decision.decisionType === DecisionType.ENGINE,
    );
    return engineDecision?.id;
  }, [decisions, selectedRuleResultType]);

  const filterRules = (
    ruleResults: IRuleResults | undefined,
  ): IRuleResults | undefined => {
    if (!ruleResults) return undefined;

    const allRuleSets = ruleResults.ruleSets || [];

    let filteredRuleSets = filterToGuidelineRuleSets(allRuleSets);

    if (selectedTab === 'RIDERS') {
      filteredRuleSets = allRuleSets.filter(ruleSet =>
        ruleSet.matchedRules.some(
          rule => (rule.ineligibleRiders ?? []).length > 0,
        ),
      );
    }

    if (selectedTab === 'HIGHLIGHT') {
      filteredRuleSets = filteredRuleSets
        .map(ruleSet => {
          const filteredMatchedRules = ruleSet.matchedRules.filter(rule => {
            return rule.rateClass !== UpdatedRateClass.PREFERRED_PLUS;
          });
          return filteredMatchedRules.length > 0
            ? { ...ruleSet, matchedRules: filteredMatchedRules }
            : null;
        })
        .filter((ruleSet): ruleSet is IRuleSet => ruleSet !== null);
    }

    return {
      ...ruleResults,
      ruleSets: sortRuleSets(filteredRuleSets),
    };
  };

  const { ruleResultsTask, isLifecycleAllowedForRuleReview, hasNoRuleReviewTask } =
    useSelector((state: RootState) => ({
      ruleResultsTask: getAllTasks(state.taskList).find(
        t => t.taskType === TaskTypes.RULE_RESULTS,
      ),
      // TODO: confirm what lifecycleV2 values are allowed to show the rule review button
      isLifecycleAllowedForRuleReview: [
        ApplicationLifecycle.PIA as string,
        ApplicationLifecycle.COMPLETED as string,
      ].includes(state.application.applicationLifecycleV2),
      hasNoRuleReviewTask:
        getAllTasks(state.taskList).find(
          t => t.taskType === TaskTypes.RULE_REVIEW,
        ) === undefined,
    }));

  const canRequestRuleReview = useAccess({
    permission: NoraPermissionV2.REQUEST_RULE_REVIEW,
  });

  const showRuleReviewButton =
    canRequestRuleReview &&
    [
      UserOrganization.ETHOS as string,
      UserOrganization.INTEGRA as string,
    ].includes(auth.userOrganization) &&
    isLifecycleAllowedForRuleReview &&
    hasNoRuleReviewTask;

  if (!ruleResultsTask) {
    return null;
  }

  const handleTabChange = (
    _: React.SyntheticEvent,
    newTabKey: RuleResultTabUnion,
  ) => {
    if (newTabKey) {
      setSelectedTab(newTabKey);
    }
  };

  const handleRequest = () => {
    if (requestDetails.trim() === '') {
      return;
    }
    dispatch(
      requestRuleReview(
        applicationId,
        { reasonDescription: requestDetails },
        openSnacks,
        setSnacks,
      ),
    );
    setIsRequestingReview(false);
  };

  const ruleResultsData = {
    fetchRuleResultsInProgress:
      selectedRuleResultType === 'Smart Retry'
        ? isSmartRetryLoading
        : isFinalLoading,
    fetchRuleResultsError:
      selectedRuleResultType === 'Smart Retry'
        ? isSmartRetryError
        : isFinalError,
    ruleResults:
      (selectedRuleResultType === 'Smart Retry'
        ? filterRules(smartRetryData)
        : filterRules(finalData)) ?? null,
  };

  return (
    <Box id={ruleResultsTask.id.toString()}>
      <Accordion sx={{ boxShadow: 'none' }} expanded>
        <AccordionSummary
          sx={{
            padding: 0,
            margin: 0,
            minHeight: '0 !important',
            '& .MuiAccordionSummary-content': {
              margin: '0 0 0 0 !important',
            },
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" flexDirection="row">
              <Box left={'-30px'} position="absolute">
                <IconButton size="small">
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    rotation={isExpanded ? 90 : undefined}
                  />
                </IconButton>
              </Box>
              <Typography variant="h2">Rule Results</Typography>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: '0px' }}>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{ marginBlock: 2 }}
          >
            <FormControl size="small">
              <Select
                sx={{ width: '150px' }}
                value={selectedRuleResultType}
                onChange={(event: SelectChangeEvent) =>
                  setSelectedRuleResultType(
                    event.target.value as RuleResultType,
                  )
                }
              >
                {availableRuleResultTypes.map(item => (
                  <MenuItem value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDecisionId && (
              <Box sx={{ width: '170px' }}>
                <EdsButton
                  buttonTitle="Decision Graph"
                  variant="infoSolid"
                  onClick={() =>
                    window.open(
                      `/workbench/ppwb/policy/${termPolicyId}/decision/${selectedDecisionId}/graph`,
                      '_blank',
                    )
                  }
                />
              </Box>
            )}
          </Box>

          {isExpanded && (
            <Box>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
              >
                {tabs.map(tabKey => (
                  <Tab
                    key={tabKey}
                    label={RuleResultTab[tabKey]}
                    value={tabKey}
                  />
                ))}
              </Tabs>
            </Box>
          )}

          <RuleResultsView
            data={ruleResultsData}
            isRidersTab={selectedTab === 'RIDERS'}
            isGen2={isGen2}
            showConditions={isExpanded}
          />

          {isExpanded && (
            <>
              {showRuleReviewButton && (
                <Box mt={2}>
                  <NoraButton
                    className={styles.RuleReviewButton}
                    onClick={handleRequestReview}
                  >
                    Request rule review
                  </NoraButton>
                </Box>
              )}
              <Dialog open={isRequestingReview} onClose={handleClose}>
                <DialogTitle>Request Rule Review</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Please input details to review below. Once request created,
                    this application will be assigned to the reviewer.
                  </DialogContentText>
                  <TextField
                    autoFocus
                    multiline
                    margin="dense"
                    id="name"
                    label="Request Details"
                    fullWidth
                    variant="standard"
                    onChange={e => setRequestDetails(e.target.value)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose}>Cancel</Button>
                  <Button onClick={handleRequest}>Request</Button>
                </DialogActions>
              </Dialog>
            </>
          )}
          {tabMessages.map((message, index) => (
            <Box
              display="flex"
              alignItems="center"
              key={index}
              gap={1}
              sx={{ marginBlock: 2 }}
            >
              <FaLightbulb color="orange" size={20} />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  fontSize: '1.2rem',
                  verticalAlign: 'text-bottom',
                }}
              >
                {message}
              </Typography>
            </Box>
          ))}

          {ruleResultsData?.ruleResults?.missingEvidences &&
            ruleResultsData.ruleResults.missingEvidences.some(
              evidence =>
                evidence.reason === 'failed' || evidence.reason === 'timeout',
            ) && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ marginBlock: 2 }}
              >
                <FaLightbulb color="orange" />
                <Typography
                  variant="body1"
                  color="textSecondary"
                  style={{ fontSize: '1.2rem' }}
                >
                  {'Missing evidence(s) at the time of evaluation'}
                </Typography>
              </Box>
            )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
